import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { Button } from '../../components/ui/button';
import { ArrowRight, Target, Heart, Lightbulb } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: "Purpose-Built",
      description: "We don't try to be everything for everyone. Orchard LMS is built specifically for landscaping companies with recurring, route-based services."
    },
    {
      icon: Heart,
      title: "Customer-First",
      description: "Every feature we build starts with understanding how landscapers actually work. We listen, learn, and build what matters."
    },
    {
      icon: Lightbulb,
      title: "Simplicity",
      description: "Powerful doesn't have to mean complicated. We believe in clean interfaces that get out of your way and let you focus on your business."
    }
  ];

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="pt-16 pb-20 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
              Built by People Who Understand Landscaping
            </h1>
            <p className="text-xl text-gray-600">
              Orchard LMS was born from frustration with generic business software that didn't 
              understand the unique needs of landscaping companies.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  Landscaping isn't like other businesses. You're not just selling products or providing 
                  one-time services. You're managing routes, recurring schedules, multiple properties 
                  per customer, and field crews who need real-time information.
                </p>
                <p>
                  We saw landscapers struggling with software that was either too simple (spreadsheets, 
                  paper) or too complex (enterprise systems designed for other industries). There had 
                  to be a better way.
                </p>
                <p>
                  Orchard LMS is that better way. We built software that thinks like a landscaper: 
                  properties are the center of everything, routes are king, and crews need simple 
                  tools that work in the field.
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1764969370921-7c8df49171cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtYW5pY3VyZWQlMjBsYXduJTIwYmFja3lhcmQlMjBncmVlbiUyMGdyYXNzfGVufDB8fHx8MTc3Mzc5NDY5M3ww&ixlib=rb-4.1.0&q=85" 
                alt="Beautifully maintained lawn" 
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What We Believe</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our values guide everything we build and how we work with our customers.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <value.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
          <p className="text-xl text-gray-600 mb-8">
            To give every landscaping company—from solo operators to multi-crew operations—the 
            tools they need to run efficiently, delight customers, and grow their business.
          </p>
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="https://images.unsplash.com/photo-1690068023694-053da714f95f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxsYXduJTIwbW93ZXIlMjBncmVlbiUyMGdyYXNzJTIwY3V0dGluZ3xlbnwwfHx8fDE3NzM3OTQ2OTR8MA&ixlib=rb-4.1.0&q=85" 
              alt="Lawn care service" 
              className="w-full h-96 object-cover"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Join the Orchard Family
          </h2>
          <p className="text-xl text-white/80 mb-8">
            We're building the future of landscaping business software—and we'd love to have you along for the ride.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default AboutPage;
